import { useState } from 'react'
import { dureeLisible, tourneeLivreur, traceDeTournee, type TraceCarte, type Tournee } from '../api/itineraire'
import type { Course, Livreur } from '../api/types'
import { Icone } from './Icone'

const EN_COURS = ['assignee', 'collecte', 'en_livraison']
const s = (n: number) => (n > 1 ? 's' : '')
const km = (n: number) => `${n.toFixed(1).replace('.', ',')} km`
const heure = (depuis: number, secondes: number) =>
  new Date(depuis + secondes * 1000).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Bamako' })

const INDISPONIBLE = 'Le service d’itinéraires ne répond pas pour le moment. Réessayez dans un instant.'

type Etat = { statut: 'chargement' } | { statut: 'ok'; t: Tournee; le: number } | { statut: 'erreur'; message: string }

/**
 * Tournées : pour chaque livreur qui a des courses en cours, « Optimiser la tournée » demande au service d'itinéraires l'ordre de
 * passage le plus court (chaque colis collecté avant d'être livré), avec les heures d'arrivée estimées à moto. C'est un ORDRE
 * CONSEILLÉ, à transmettre au livreur : rien n'est modifié dans les courses.
 */
export function TourneesLivreurs({
  livreurs,
  courses,
  traceActive,
  surTracer,
}: {
  livreurs: Livreur[]
  courses: Course[]
  /** Titre du tracé affiché sur la carte, pour marquer la tournée visible. */
  traceActive: string | null
  surTracer: (t: TraceCarte | null) => void
}) {
  const [etats, setEtats] = useState<Record<string, Etat>>({})
  const occupes = livreurs
    .map((l) => ({ l, n: courses.filter((c) => c.driver_id === l.id && EN_COURS.includes(c.statut)).length }))
    .filter((x) => x.n > 0)
    .sort((a, b) => b.n - a.n || a.l.full_name.localeCompare(b.l.full_name, 'fr'))

  async function optimiser(l: Livreur) {
    setEtats((e) => ({ ...e, [l.id]: { statut: 'chargement' } }))
    try {
      const t = await tourneeLivreur(l.id)
      setEtats((e) => ({ ...e, [l.id]: { statut: 'ok', t, le: Date.now() } }))
      if (t.etapes.length > 0) surTracer(traceDeTournee(t))
    } catch (err) {
      /* Les erreurs réseau de la bibliothèque sont en anglais : on n'affiche que nos propres phrases. */
      const brut = err instanceof Error ? err.message : ''
      const message = !brut || /edge function|failed to fetch|networkerror|non-2xx/i.test(brut) ? INDISPONIBLE : brut
      setEtats((e) => ({ ...e, [l.id]: { statut: 'erreur', message } }))
    }
  }

  if (occupes.length === 0) return <div className="no-hit">Aucun livreur n’a de course en cours.</div>

  return (
    <div className="tournees">
      {occupes.map(({ l, n }) => {
        const e = etats[l.id]
        const titre = `Tournée de ${l.full_name}`
        return (
          <div className="tournee" key={l.id}>
            <div className="tournee-tete">
              <div>
                <b>{l.full_name}</b>
                <div className="t-muted" style={{ fontSize: 12 }}>
                  {n} course{s(n)} en cours
                </div>
              </div>
              <button className="btn btn-outline btn-sm" disabled={e?.statut === 'chargement'} onClick={() => void optimiser(l)}>
                <Icone nom="spark" /> {e?.statut === 'chargement' ? 'Calcul…' : e?.statut === 'ok' ? 'Recalculer' : 'Optimiser la tournée'}
              </button>
            </div>

            {e?.statut === 'erreur' && (
              <div className="bandeau erreur" role="alert" style={{ marginTop: 10, marginBottom: 0 }}>
                {e.message}
              </div>
            )}

            {e?.statut === 'ok' && (
              <>
                {e.t.etapes.length === 0 ? (
                  <div className="t-muted" style={{ marginTop: 10, fontSize: 13 }}>
                    Aucune étape à planifier : les points de ces courses ne sont pas encore connus.
                  </div>
                ) : (
                  <ol className="tournee-etapes" aria-label={`Ordre conseillé pour ${l.full_name}`}>
                    {e.t.etapes.map((x, i) => (
                      <li key={`${x.course}-${x.type}`}>
                        <span className={`arret-trace${x.type === 'livraison' ? ' plein' : ''}`}>{i + 1}</span>
                        <span>
                          {x.type === 'collecte' ? 'Collecte' : 'Livraison'} <b>{x.code}</b>
                        </span>
                        <time>{heure(e.le, x.arrivee_s)}</time>
                      </li>
                    ))}
                  </ol>
                )}
                {(e.t.non_planifiees.length > 0 || e.t.sans_position.length > 0) && (
                  <div className="t-muted" style={{ marginTop: 8, fontSize: 12 }}>
                    {e.t.sans_position.length > 0 && <>Sans point sur la carte : {e.t.sans_position.join(', ')}. </>}
                    {e.t.non_planifiees.length > 0 && <>Non planifiées : {e.t.non_planifiees.join(', ')}.</>}
                  </div>
                )}
                <div className="tournee-pied">
                  {e.t.etapes.length > 0 && (
                    <span className="t-muted" style={{ fontSize: 12 }}>
                      {km(e.t.distance_km)} · {dureeLisible(e.t.duree_s)} à moto
                      {e.t.depart?.source === 'premiere_collecte' ? ' · départ à la première collecte (position du livreur inconnue)' : ''}
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  {e.t.etapes.length > 0 &&
                    (traceActive === titre ? (
                      <button className="btn btn-outline btn-sm" onClick={() => surTracer(null)}>
                        Masquer sur la carte
                      </button>
                    ) : (
                      <button className="btn btn-outline btn-sm" onClick={() => surTracer(traceDeTournee(e.t))}>
                        <Icone nom="pin" /> Voir sur la carte
                      </button>
                    ))}
                </div>
              </>
            )}
          </div>
        )
      })}
      <div className="t-muted" style={{ fontSize: 12 }}>
        Ordre conseillé, à transmettre au livreur : les courses ne sont pas modifiées. Itinéraires © contributeurs OpenStreetMap.
      </div>
    </div>
  )
}
